import React from 'react'
import { NextPage } from 'next'
import dynamic from 'next/dynamic'
import { useSelector } from 'react-redux'
import { Box, useBreakpointValue } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { wrapper } from 'src/redux'
import { ActionBar } from 'src/components/NltOffers/'
import { Head, MetaTags, LinkRel } from 'src/components/common'
import { getNltPagesData } from 'src/utils/getStaticProps'
import { useBackButton } from 'src/utils/hooks'
import { PAGE_CACHE_CONTROL } from 'server/constants'

const NltContentInfo = dynamic(() => import('src/components/Sections/NltContentInfo'))
const MobileActionBar = dynamic(() => import('src/components/NltOffers/MobileActionBar'))
const NltOffersContainer = dynamic(() => import('src/containers/NltOffersContainer'))
const PageHeaderContainer = dynamic(() => import('src/containers/PageHeaderContainer'))

interface Props {
  page: any
}

const NltSearchPage: NextPage<Props> = ({ page }) => {
  const { t } = useTranslation()
  const visibleOnMobile = useBreakpointValue({ base: true, md: false })
  const requestedPage = useSelector((state: Store.RootState) => state.nlt.listingPage)

  useBackButton()

  const crumbs = [
    {
      page: 'Home Page',
      href: '/',
      title: 'Home Page',
    },
    {
      page: 'Offerte Noleggio a Lungo Termine',
      href: '/noleggio-a-lungo-termine',
      title: 'Offerte Noleggio a Lungo Termine',
    },
  ]

  const metaPage = Math.max(page || 0, requestedPage || 0, 1)

  return (
    <Box bgColor="baseNeutral.100">
      <style global jsx>{`
        @media (max-width: 600px) {
          #CookiebotWidget {
            bottom: 66px !important;
            z-index: calc(var(--cp-zIndices-modal) - 1) !important;
          }
        }
      `}</style>
      <Head title={t('nltPage:header.title', { page: metaPage && metaPage > 1 ? ` pag. ${metaPage}` : '' })} />
      <MetaTags meta={
        metaPage && metaPage > 1
          ? t(`nltPage:metaNextPages`, { returnObjects: true, page: metaPage })
          : t(`nltPage:meta`, { returnObjects: true })}
      />
      <LinkRel
        rel="canonical"
        href={`https://www.carplanner.com/noleggio-a-lungo-termine${page && page > 1 ? `?pagina=${page}` : ''}`}
      />

      <PageHeaderContainer
        title={t('nltPage:header.title')}
        description={t('nltPage:header.description')}
        crumbs={crumbs}
        linkHref="/come-funziona-noleggio-a-lungo-termine"
        linkTitle={t('nltPage:header.howItWorksLink')}
        domain="rent"
      />

      <Box w="100%" display="inline-block" pb="24px" pl="1.5rem">
        <ActionBar />

        <NltOffersContainer />
      </Box>

      {requestedPage < 2 &&
        <NltContentInfo
          title={t('nltPage:infoSection.title')}
          description={t('nltPage:infoSection.text')}
          asHtml
        />
      }

      {visibleOnMobile &&
        <MobileActionBar />
      }

    </Box>
  )
}

export const getServerSideProps = wrapper.getServerSideProps(store => async ({ query, res }) => {
  const props = await getNltPagesData(store, query)

  res.setHeader("Cache-Control", PAGE_CACHE_CONTROL);

  return { props: { ...props } }
})

export default NltSearchPage
